/**
 * AbsenceDialog — Erfassen/Bearbeiten einer Abwesenheit.
 *
 * Neu (2026-Feb):
 *  - Bei „Krankheit" (K): Auswahl AUB / eAUB / Noch offen — wichtig fuer
 *    woechentliche Meldung an die Lohnabteilung.
 *  - Bei K und KK: Datei-Upload (Drag&Drop, mehrere Dateien moeglich).
 */
import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { formatLastFirst, getDuplicateNameKeys } from '../../lib/nameFormat';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { DatePickerDE } from '../../components/ui/date-picker-de';
import { FileDropzone } from '../../components/ui/file-dropzone';
import { Loader2, Paperclip, Trash2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { uploadAPI } from '../../lib/api';
import { openAuthFile } from '../../lib/openAuthFile';
import { ABSENCE_TYPES, CERTIFICATE_TYPES } from './constants';

export const AbsenceDialog = ({
  open, onOpenChange, formData, setFormData, employees,
  isEdit, saving, onSubmit, koTagEnabled = true,
}) => {
  const [uploading, setUploading] = useState(false);

  const setField = (field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const visibleTypes = ABSENCE_TYPES.filter(
    (t) => t.value !== 'KO' || koTagEnabled || formData.absence_type === 'KO'
  );

  const supportsAttachments = formData.absence_type === 'K' || formData.absence_type === 'KK';
  const attachments = formData.attachments || [];

  const selectedEmployee = employees.find((e) => e.id === formData.employee_id);
  const dupKeys = React.useMemo(() => getDuplicateNameKeys(employees), [employees]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const empName = selectedEmployee
        ? `${selectedEmployee.last_name || ''}_${selectedEmployee.first_name || ''}`.trim()
        : '';
      const titleHint = formData.absence_type === 'K'
        ? (formData.certificate_type === 'eAUB' ? 'eAUB' : 'AUB')
        : 'Kind-Attest';
      const uploaded = [];
      for (const file of files) {
        const res = await uploadAPI.uploadCertificate(file, {
          employee_name: empName,
          document_title: titleHint,
          document_date: formData.start_date || '',
        });
        uploaded.push({
          filename: res.data.filename,
          original_filename: res.data.original_filename || file.name,
          url: res.data.url,
          size: res.data.size,
          content_type: file.type || null,
          uploaded_at: new Date().toISOString(),
        });
      }
      setField('attachments', [...attachments, ...uploaded]);
      toast.success(uploaded.length === 1 ? 'Datei hochgeladen' : `${uploaded.length} Dateien hochgeladen`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (idx) => {
    setField('attachments', attachments.filter((_, i) => i !== idx));
  };

  const openAttachment = (att) => {
    openAuthFile(att.url, { filename: att.original_filename }).catch(() => {
      toast.error('Datei konnte nicht geöffnet werden');
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {isEdit ? 'Abwesenheit bearbeiten' : 'Abwesenheit erfassen'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label>Mitarbeiter *</Label>
            <Select
              value={formData.employee_id}
              onValueChange={(v) => setField('employee_id', v)}
            >
              <SelectTrigger data-testid="absence-employee-select">
                <SelectValue placeholder="Mitarbeiter auswählen" />
              </SelectTrigger>
              <SelectContent>
                {employees.map((emp) => (
                  <SelectItem key={emp.id} value={emp.id}>
                    {formatLastFirst(emp, dupKeys)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Art der Abwesenheit *</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {visibleTypes.map((type) => (
                <Button
                  key={type.value}
                  type="button"
                  variant={formData.absence_type === type.value ? 'default' : 'outline'}
                  className="flex-col h-auto py-3"
                  onClick={() => setField('absence_type', type.value)}
                  data-testid={`absence-type-${type.value}`}
                >
                  <type.icon className="h-5 w-5 mb-1" />
                  <span className="text-xs font-bold">{type.short}</span>
                  <span className="text-xs opacity-80 text-center leading-tight">{type.label}</span>
                </Button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start_date">Von *</Label>
              <DatePickerDE
                value={formData.start_date}
                onChange={(v) => setField('start_date', v)}
                placeholder="Startdatum"
                testId="absence-start-date"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end_date">Bis *</Label>
              <DatePickerDE
                value={formData.end_date}
                onChange={(v) => setField('end_date', v)}
                placeholder="Enddatum"
                testId="absence-end-date"
              />
            </div>
          </div>

          {formData.absence_type === 'K' && (
            <div className="p-3 rounded-lg bg-secondary/30 space-y-3">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Bescheinigung *</Label>
                <p className="text-xs text-muted-foreground -mt-1">
                  Wichtig für die wöchentliche Meldung an die Lohnabteilung.
                </p>
                <div className="grid grid-cols-3 gap-2 pt-1">
                  {CERTIFICATE_TYPES.map((ct) => (
                    <Button
                      key={ct.value}
                      type="button"
                      variant={formData.certificate_type === ct.value ? 'default' : 'outline'}
                      className="flex-col h-auto py-2"
                      onClick={() => {
                        setField('certificate_type', ct.value);
                        setField('doctor_note', ct.value !== 'none');
                      }}
                      data-testid={`absence-certificate-type-${ct.value}`}
                    >
                      <span className="text-xs font-bold">{ct.short}</span>
                      <span className="text-[10px] opacity-80 text-center leading-tight">
                        {ct.value === 'eAUB' ? 'elektronisch' : ct.value === 'AUB' ? 'Papier' : '—'}
                      </span>
                    </Button>
                  ))}
                </div>
              </div>
              {formData.certificate_type && formData.certificate_type !== 'none' && (
                <div className="space-y-2">
                  <Label htmlFor="doctor_note_date">Attest-Datum</Label>
                  <DatePickerDE
                    value={formData.doctor_note_date}
                    onChange={(v) => setField('doctor_note_date', v)}
                    placeholder="Attest-Datum"
                  />
                </div>
              )}
            </div>
          )}

          {formData.absence_type === 'KK' && (
            <div className="p-3 rounded-lg bg-secondary/30 space-y-3">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="child_sick_certificate"
                  checked={formData.child_sick_certificate}
                  onCheckedChange={(v) => setField('child_sick_certificate', v)}
                  data-testid="absence-child-sick-certificate"
                />
                <Label htmlFor="child_sick_certificate" className="font-normal">
                  Attest liegt vor
                </Label>
              </div>
            </div>
          )}

          {formData.absence_type === 'EZ' && (
            <div className="p-3 rounded-lg bg-secondary/30 space-y-3">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="employer_confirmation"
                  checked={formData.employer_confirmation}
                  onCheckedChange={(v) => setField('employer_confirmation', v)}
                  data-testid="absence-employer-confirmation"
                />
                <Label htmlFor="employer_confirmation" className="font-normal">
                  Bestätigung vom Arbeitgeber liegt vor
                </Label>
              </div>
            </div>
          )}

          {formData.absence_type === 'KO' && (
            <div className="p-3 rounded-lg bg-rose-50 dark:bg-rose-950/20 border border-rose-200 dark:border-rose-900/40 text-xs text-rose-900 dark:text-rose-200">
              <p className="font-medium mb-1">Hinweis: Krank ohne Krankenschein (KO-Tag)</p>
              <p>Mitarbeiter hat sich krankgemeldet, ohne ein Attest einzureichen. Der Eintrag erscheint im Team-Kalender und in der Statistik unter „KO-Tag".</p>
            </div>
          )}

          {supportsAttachments && (
            <div className="space-y-2" data-testid="absence-attachments-section">
              <Label className="flex items-center gap-2">
                <Paperclip className="h-4 w-4" />
                {formData.absence_type === 'K' ? 'AUB / eAUB-Scan' : 'Attest hochladen'}
              </Label>
              {attachments.length > 0 && (
                <div className="space-y-1.5">
                  {attachments.map((att, idx) => (
                    <div
                      key={`${att.filename}-${idx}`}
                      className="flex items-center gap-2 p-2 rounded-md border border-border bg-secondary/30"
                      data-testid={`absence-attachment-${idx}`}
                    >
                      <FileText className="h-4 w-4 text-primary shrink-0" />
                      <button
                        type="button"
                        onClick={() => openAttachment(att)}
                        className="flex-1 text-left text-sm font-medium truncate hover:text-primary hover:underline"
                        title={att.original_filename}
                      >
                        {att.original_filename}
                      </button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive shrink-0"
                        onClick={() => removeAttachment(idx)}
                        data-testid={`absence-attachment-remove-${idx}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <FileDropzone
                accept=".pdf,.jpg,.jpeg,.png"
                onUpload={handleUpload}
                uploading={uploading}
                multiple
                hint="PDF, JPG oder PNG · max. 10 MB · mehrere Dateien möglich"
                testId="absence-file-dropzone"
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="notes">Notizen</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setField('notes', e.target.value)}
              placeholder="Optionale Anmerkungen..."
              rows={2}
              data-testid="absence-notes"
            />
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-full w-full sm:w-auto"
            >
              Abbrechen
            </Button>
            <Button
              onClick={onSubmit}
              disabled={saving || uploading}
              className="rounded-full w-full sm:w-auto"
              data-testid="save-absence-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Speichern...
                </>
              ) : (
                isEdit ? 'Aktualisieren' : 'Erfassen'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
